import { useEffect, useState } from 'react';
import { AnalyticsPixelIds } from 'core/analytics/entities';
import { useTrackArrival } from 'core/analytics/hooks';
import { initDependencies } from 'core/common/contexts';
import { useClearQueryParams, useIsomorphicLayoutEffect, useUrlLocation } from 'core/common/hooks';
import { type RootStore } from 'core/common/store';
import createStore from 'core/common/store/store';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import { getThemeType } from 'core/theme_deprecated/utils/getFlowThemeType';
import { config } from 'src/config';

type UseAppParams = {
  analyticsPixelIds: AnalyticsPixelIds;
  features: FeatureFlagsConfig['features'];
};

export const useApp = ({ analyticsPixelIds, features }: UseAppParams) => {
  const { location: urlLocation } = useUrlLocation();

  const theme = getThemeType(urlLocation);
  const [services] = useState(() => initDependencies(config, { analyticsPixelIds, features }));

  const { featureFlags, cookieConsentService, utmTagsService, appEnvironment, arrivalService } =
    services;

  useTrackArrival(arrivalService);

  useClearQueryParams(utmTagsService, appEnvironment);

  const [appStore] = useState<RootStore>(() => createStore(services));

  useEffect(() => {
    cookieConsentService.initialize();
  }, [cookieConsentService]);

  useIsomorphicLayoutEffect(() => {
    services.loggingService.info('App services initialized');
    // eslint-disable-next-line no-console
    console.log('✅ Version:', config.appVersion);
  }, []);

  useEffect(() => {
    const deviceId = services.analyticsService.getDeviceId();
    services.errorMonitoringService.identify(deviceId);
  }, [services.analyticsService, services.errorMonitoringService]);

  useEffect(() => {
    featureFlags.onAttributesUpdated((attributes) => {
      services.analyticsService.setFeatureFlagsAttributes(attributes);
    });

    services.heatmapRecordingService.initialize();
  }, [services, featureFlags]);

  useEffect(() => {
    if (!window.location.hash) {
      window.scroll(0, 0);
    }
  }, [urlLocation.pathname]);

  return {
    config: config,
    appStore,
    theme,
    featureFlags,
    services,
  };
};
